import React, { useEffect, useState } from "react";
import MrvSidebar from "../mrv/MrvSidebar";
import Sidebar from "../menu/Sidebar";
import SidebarAdmin from "../menu/SidebarAdmin";
import Topbar from "../menu/Topbar";
import RealgemsSidebar from "../realgems/RealgemsSidebar";
import Api from "../Services/Api";
import { useAuth } from "../auth/AuthContext";
import { canAccessAdminArea, canAccessRealgemsArea, normalizeAccessType } from "../../utils/accessControl";
import "./MyProfile.css";

const onlyDigits = (value = "") => String(value).replace(/\D/g, "");

const formatCpfCnpj = (value = "") => {
  const digits = onlyDigits(value).slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
  }
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5");
};

const formatPhone = (value = "") => {
  const digits = onlyDigits(value).slice(0, 11);
  return digits
    .replace(/^(\d{2})(\d)/, "($1) $2")
    .replace(/^(\(\d{2}\) \d{5})(\d)/, "$1-$2");
};

const MyProfile = () => {
  const { user, updateUser } = useAuth();
  const [menuClose, setMenuClose] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", cpf_cnpj: "", phone: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const accessType = normalizeAccessType(user?.access_type);
  const isAdmin = canAccessAdminArea(accessType);
  const isRealgems = canAccessRealgemsArea(accessType);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await Api.get("/auth/my-profile");
        const data = response.data?.user || {};
        setForm({
          full_name: data.full_name || "",
          email: data.email || "",
          cpf_cnpj: formatCpfCnpj(data.cpf_cnpj),
          phone: formatPhone(data.phone),
        });
      } catch (err) {
        setError(err?.response?.data?.message || "Nao foi possivel carregar seus dados.");
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (!form.full_name.trim()) {
      setError("Informe seu nome.");
      return;
    }

    try {
      setSaving(true);
      const response = await Api.put("/auth/my-profile", {
        full_name: form.full_name.trim(),
        cpf_cnpj: onlyDigits(form.cpf_cnpj),
        phone: onlyDigits(form.phone),
      });
      const updatedUser = response.data?.user;
      if (updatedUser) {
        updateUser(updatedUser);
        setForm({
          full_name: updatedUser.full_name || "",
          email: updatedUser.email || "",
          cpf_cnpj: formatCpfCnpj(updatedUser.cpf_cnpj),
          phone: formatPhone(updatedUser.phone),
        });
      }
      setSuccess(response.data?.message || "Dados atualizados com sucesso.");
    } catch (err) {
      setError(err?.response?.data?.message || "Nao foi possivel atualizar seus dados.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`app-container${isRealgems ? " realgems-dashboard-page" : ""}`}>
      {isAdmin ? <SidebarAdmin close={menuClose} /> : isRealgems ? <RealgemsSidebar close={menuClose} /> : (accessType === "MRV" ? <MrvSidebar close={menuClose} /> : <Sidebar close={menuClose} />)}
      <main className={`main-content${isRealgems ? " realgems-main-content" : ""}`}>
        <Topbar page="Minha conta / Meus dados" onToggleMenu={() => setMenuClose((current) => !current)} />
        <section className="my-profile-page">
          <article className="my-profile-card">
            <header className="my-profile-header">
              <span>Minha conta</span>
              <h1>Meus dados</h1>
              <p>Confira e mantenha seus dados cadastrais atualizados.</p>
            </header>
            {loading ? <p className="my-profile-state">Carregando seus dados...</p> : (
              <form className="my-profile-form" onSubmit={handleSubmit}>
                <label><span>Nome</span><input value={form.full_name} onChange={(event) => setForm((current) => ({ ...current, full_name: event.target.value }))} autoComplete="name" /></label>
                <label><span>E-mail</span><input value={form.email} disabled title="O e-mail não pode ser alterado por esta tela." /></label>
                <label><span>CPF ou CNPJ</span><input value={form.cpf_cnpj} onChange={(event) => setForm((current) => ({ ...current, cpf_cnpj: formatCpfCnpj(event.target.value) }))} inputMode="numeric" placeholder="Digite seu CPF ou CNPJ" /></label>
                <label><span>WhatsApp</span><input value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: formatPhone(event.target.value) }))} inputMode="tel" placeholder="(00) 00000-0000" autoComplete="tel" /></label>
                {error ? <div className="my-profile-message error">{error}</div> : null}
                {success ? <div className="my-profile-message success">{success}</div> : null}
                <div className="my-profile-actions"><button type="submit" disabled={saving}>{saving ? "Salvando..." : "Salvar alterações"}</button></div>
              </form>
            )}
          </article>
        </section>
      </main>
    </div>
  );
};

export default MyProfile;
